import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  DollarSign,
  Users,
  AlertTriangle,
  CheckCircle,
  Download,
  TrendingUp,
  Package,
  Clock,
  XCircle,
  Eye,
  ExternalLink,
  Info
} from 'lucide-react';
import { IntegrationService } from '../../services/IntegrationService';
import { PatientService } from '../../services/PatientService';
import { MonthlyClosingSummary, MonthlyClosingItem } from '../../types/integration';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { formatDateForDisplayFromString } from '../../utils/dateHelpers';

const MonthlyClosingPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedClient } = useAuth();
  const clientId = selectedClient?.clientId;
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [summary, setSummary] = useState<MonthlyClosingSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<MonthlyClosingItem | null>(null);
  const [showActivePatientsInfo, setShowActivePatientsInfo] = useState(false);
  const [showSessionsRealizedInfo, setShowSessionsRealizedInfo] = useState(false);
  const [showRevenueTotalInfo, setShowRevenueTotalInfo] = useState(false);
  const [showProfessionalPayoutsInfo, setShowProfessionalPayoutsInfo] = useState(false);
  const formatPeriodDate = (value?: string) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return formatDateForDisplayFromString(value);
    }
    const isoPrefix = /^(\d{4}-\d{2}-\d{2})T/.exec(value);
    if (isoPrefix?.[1]) {
      return formatDateForDisplayFromString(isoPrefix[1]);
    }
    return formatDateForDisplayFromString(value);
  };

  useEffect(() => {
    if (!clientId) {
      setSummary(null);
      return;
    }
    loadClosingSummary();
  }, [selectedMonth, clientId]);

  const loadClosingSummary = async () => {
    try {
      if (!clientId) {
        return;
      }
      setLoading(true);
      console.log(`📊 Carregando fechamento para ${selectedMonth}...`);
      const closingSummary = await IntegrationService.generateMonthlyClosing(selectedMonth, clientId);
      setSummary(closingSummary);
      console.log('✅ Fechamento carregado:', closingSummary);
    } catch (error) {
      console.error('Erro ao carregar fechamento:', error);
      toast.error('Erro ao carregar fechamento mensal');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!summary) return;

    const csvHeaders = [
      'Paciente',
      'Tipo',
      'Sessões Agendadas',
      'Sessões Realizadas',
      'Faltas',
      'Pacote (R$)',
      'Sessões Individuais (R$)',
      'Sessões Adicionais (R$)',
      'Cobranças por Faltas (R$)',
      'Total (R$)',
      'Alertas'
    ];

    const csvData = summary.items.map(item => [
      item.patientName,
      item.hasPackage ? 'Pacote' : 'Avulso',
      item.sessions.scheduled,
      item.sessions.completed,
      item.sessions.noShow,
      item.billing.packageAmount.toFixed(2),
      item.billing.individualSessionsAmount.toFixed(2),
      item.billing.extraSessionsAmount.toFixed(2),
      item.billing.absenceCharges.toFixed(2),
      item.billing.total.toFixed(2),
      item.alerts.join('; ')
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `fechamento_${selectedMonth}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Relatório exportado!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando fechamento mensal...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fechamento Financeiro Mensal</h1>
          <p className="text-gray-600 mt-1">Relatório de conferência e exportação do fechamento</p>
        </div>
        <div className="flex gap-3">
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={() => navigate(`/admin/financial-new?tab=invoices&dueMonth=${selectedMonth}`)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            title="Ver todas as faturas geradas"
          >
            <ExternalLink className="w-4 h-4" />
            Ver Faturas
          </button>
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <span className="font-medium text-blue-900">
            Período: {formatPeriodDate(summary.period.startDate)} até {formatPeriodDate(summary.period.endDate)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
                <span>Pacientes Ativos</span>
                <div
                  className="relative inline-flex"
                  onMouseEnter={() => setShowActivePatientsInfo(true)}
                  onMouseLeave={() => setShowActivePatientsInfo(false)}
                >
                  <button
                    type="button"
                    className="inline-flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Definição de pacientes ativos no período"
                    aria-expanded={showActivePatientsInfo}
                    onClick={() => setShowActivePatientsInfo((prev) => !prev)}
                    onFocus={() => setShowActivePatientsInfo(true)}
                    onBlur={() => setShowActivePatientsInfo(false)}
                  >
                    <Info className="w-4 h-4 cursor-help" />
                  </button>
                  <div
                    className={`pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-80 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg transition-opacity duration-150 ${
                      showActivePatientsInfo ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    Pacientes ativos no período: possuem ao menos 1 agendamento não cancelado no mês selecionado ou pacote ativo em algum dia do período.
                  </div>
                </div>
              </div>
              <p className="text-2xl font-bold text-green-600">{summary.overview.activePatients}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.overview.totalPatients} total</p>
            </div>
            <Users className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
                <span>Sessões Realizadas</span>
                <div
                  className="relative inline-flex"
                  onMouseEnter={() => setShowSessionsRealizedInfo(true)}
                  onMouseLeave={() => setShowSessionsRealizedInfo(false)}
                >
                  <button
                    type="button"
                    className="inline-flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Definição de sessões realizadas no período"
                    aria-expanded={showSessionsRealizedInfo}
                    onClick={() => setShowSessionsRealizedInfo((prev) => !prev)}
                    onFocus={() => setShowSessionsRealizedInfo(true)}
                    onBlur={() => setShowSessionsRealizedInfo(false)}
                  >
                    <Info className="w-4 h-4 cursor-help" />
                  </button>
                  <div
                    className={`pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-80 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg transition-opacity duration-150 ${
                      showSessionsRealizedInfo ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    Sessões realizadas: total de agendamentos no período do fechamento com status diferente de cancelado e falta.
                  </div>
                </div>
              </div>
              <p className="text-2xl font-bold text-blue-600">{summary.appointments.completed}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.appointments.total} agendadas</p>
            </div>
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
                <span>Receita Total</span>
                <div
                  className="relative inline-flex"
                  onMouseEnter={() => setShowRevenueTotalInfo(true)}
                  onMouseLeave={() => setShowRevenueTotalInfo(false)}
                >
                  <button
                    type="button"
                    className="inline-flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Definição de receita total no período"
                    aria-expanded={showRevenueTotalInfo}
                    onClick={() => setShowRevenueTotalInfo((prev) => !prev)}
                    onFocus={() => setShowRevenueTotalInfo(true)}
                    onBlur={() => setShowRevenueTotalInfo(false)}
                  >
                    <Info className="w-4 h-4 cursor-help" />
                  </button>
                  <div
                    className={`pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-80 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg transition-opacity duration-150 ${
                      showRevenueTotalInfo ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    Receita Total: soma das faturas com vencimento no período selecionado, excluindo status cancelado.
                  </div>
                </div>
              </div>
              <p className="text-2xl font-bold text-purple-600">
                {summary.financial.revenueByDueDate.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </p>
              <p className="text-xs text-gray-500 mt-1">Vencimento no período</p>
            </div>
            <DollarSign className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
                <span>Repasses Totais</span>
                <div
                  className="relative inline-flex"
                  onMouseEnter={() => setShowProfessionalPayoutsInfo(true)}
                  onMouseLeave={() => setShowProfessionalPayoutsInfo(false)}
                >
                  <button
                    type="button"
                    className="inline-flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Definição de repasses totais no período"
                    aria-expanded={showProfessionalPayoutsInfo}
                    onClick={() => setShowProfessionalPayoutsInfo((prev) => !prev)}
                    onFocus={() => setShowProfessionalPayoutsInfo(true)}
                    onBlur={() => setShowProfessionalPayoutsInfo(false)}
                  >
                    <Info className="w-4 h-4 cursor-help" />
                  </button>
                  <div
                    className={`pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-80 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg transition-opacity duration-150 ${
                      showProfessionalPayoutsInfo ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    Soma dos repasses com pagamento efetivado no período selecionado.
                  </div>
                </div>
              </div>
              <p className="text-2xl font-bold text-yellow-600">
                {summary.financial.totalProfessionalPayouts.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </p>
              <p className="text-xs text-gray-500 mt-1">Pagos no período</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Distribuição de Receita
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pacotes:</span>
              <span className="font-medium">R$ {summary.financial.packageRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Sessões Avulsas:</span>
              <span className="font-medium">R$ {summary.financial.individualRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Sessões Adicionais:</span>
              <span className="font-medium">R$ {summary.financial.extraSessionsRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Cobranças por Faltas:</span>
              <span className="font-medium">R$ {summary.financial.otherRevenue.toFixed(2)}</span>
            </div>
            <div className="pt-3 border-t flex justify-between items-center">
              <span className="font-semibold text-gray-900">Total:</span>
              <span className="font-bold text-lg">R$ {summary.financial.totalRevenue.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Tipo de Pacientes
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Com Pacote:</span>
              <div className="flex items-center gap-2">
                <span className="font-medium">{summary.overview.patientsWithPackage}</span>
                <div className="w-16 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full"
                    style={{
                      width: `${(summary.overview.patientsWithPackage / Math.max(summary.overview.activePatients, 1)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Sessões Avulsas:</span>
              <div className="flex items-center gap-2">
                <span className="font-medium">{summary.overview.patientsIndividual}</span>
                <div className="w-16 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{
                      width: `${(summary.overview.patientsIndividual / Math.max(summary.overview.activePatients, 1)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Status de Agendamentos
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Realizados:</span>
              <span className="font-medium text-green-600">{summary.appointments.completed}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Faltas:</span>
              <span className="font-medium text-red-600">{summary.appointments.noShow}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Cancelados:</span>
              <span className="font-medium text-gray-600">{summary.appointments.cancelled}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border shadow-sm">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Detalhamento por Paciente</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paciente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sessões
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Faltas
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summary.items.map((item) => (
                <tr key={item.patientId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{item.patientName}</div>
                    {item.alerts.length > 0 && (
                      <div className="text-xs text-orange-600 mt-1">
                        {item.alerts[0]}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.hasPackage ? (
                      <div className="flex items-center gap-1">
                        <Package className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-purple-600 font-medium">Pacote</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-600">Avulso</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="text-sm font-medium">{item.sessions.completed}</div>
                    <div className="text-xs text-gray-500">de {item.sessions.scheduled}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`text-sm font-medium ${item.sessions.noShow > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {item.sessions.noShow}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-bold text-gray-900">
                      R$ {item.billing.total.toFixed(2)}
                    </div>
                    {item.billing.absenceCharges > 0 && (
                      <div className="text-xs text-gray-600">
                        +R$ {item.billing.absenceCharges.toFixed(2)} (cobranças por faltas)
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {item.billing.hasPaidInvoice ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                        Pago
                      </span>
                    ) : item.billing.alreadyBilled ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Faturado
                      </span>
                    ) : item.billing.total > 0 ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        A Faturar
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                        Sem Valores
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setSelectedPatient(item)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Ver Detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedPatient.patientName}</h2>
                  <p className="text-gray-600">Detalhamento do Fechamento</p>
                </div>
                <button
                  onClick={() => setSelectedPatient(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {selectedPatient.packageInfo && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Informações do Pacote
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-purple-700 font-medium">Pacote:</span>
                        <p className="text-purple-900">{selectedPatient.packageInfo.name}</p>
                      </div>
                      <div>
                        <span className="text-purple-700 font-medium">Valor:</span>
                        <p className="text-purple-900">R$ {selectedPatient.packageInfo.price.toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="text-purple-700 font-medium">Sessões:</span>
                        <p className="text-purple-900">
                          {selectedPatient.packageInfo.sessionsUsed} / {selectedPatient.packageInfo.sessionsContracted}
                        </p>
                      </div>
                      <div>
                        <span className="text-purple-700 font-medium">Vencimento:</span>
                        <p className={`font-medium ${
                          selectedPatient.packageInfo.isExpired ? 'text-red-600' :
                          selectedPatient.packageInfo.daysUntilExpiry <= 7 ? 'text-orange-600' :
                          'text-green-600'
                        }`}>
                          {new Date(selectedPatient.packageInfo.expiryDate).toLocaleDateString('pt-BR')}
                          {selectedPatient.packageInfo.isExpired ? ' (VENCIDO)' :
                           ` (${selectedPatient.packageInfo.daysUntilExpiry} dias)`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Detalhamento Financeiro
                  </h3>
                  <div className="space-y-2 text-sm">
                    {selectedPatient.billing.packageAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-blue-700">Pacote:</span>
                        <span className="font-medium text-blue-900">R$ {selectedPatient.billing.packageAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedPatient.billing.individualSessionsAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-blue-700">Sessões Avulsas:</span>
                        <span className="font-medium text-blue-900">R$ {selectedPatient.billing.individualSessionsAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedPatient.billing.extraSessionsAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-blue-700">Sessões Adicionais:</span>
                        <span className="font-medium text-blue-900">R$ {selectedPatient.billing.extraSessionsAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedPatient.billing.absenceCharges > 0 && (
                      <div className="flex justify-between">
                        <span className="text-blue-700">Cobranças por Faltas:</span>
                        <span className="font-medium text-blue-900">R$ {selectedPatient.billing.absenceCharges.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-blue-300 flex justify-between">
                      <span className="font-semibold text-blue-900">Valor da Fatura:</span>
                      <span className="font-bold text-lg text-blue-900">R$ {selectedPatient.billing.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {(selectedPatient.absences.unexcusedAbsences ?? selectedPatient.absences.faltasNaoJustificadas ?? 0) > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Histórico de Faltas
                    </h3>
                    <div className="space-y-2">
                      {((selectedPatient.absences.absenceHistory ?? selectedPatient.absences.historicoFaltas ?? []) as any[]).map((absence, index) => (
                        <div key={index} className="flex justify-between items-center text-sm gap-4">
                          <div className="min-w-0">
                            <span className="text-red-700">
                              Falta #{absence.absenceNumber} - {new Date(absence.date).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          <div className="text-right">
                            <div className={`font-medium ${absence.status === 'charged' ? 'text-red-900' : 'text-orange-600'}`}>
                              {absence.status === 'charged' ? `R$ ${absence.charge.toFixed(2)}` : 'Sem cobrança'}
                            </div>
                            {absence.status === 'charged' && absence.invoiceDueDate && (
                              <div className="text-xs text-red-500 mt-1">
                                Fatura com vencimento em {formatPeriodDate(absence.invoiceDueDate)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedPatient.alerts.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h3 className="font-semibold text-orange-900 mb-2">Alertas</h3>
                    <ul className="space-y-1 text-sm text-orange-800">
                      {selectedPatient.alerts.map((alert, index) => (
                        <li key={index}>{alert}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-between gap-3">
                <button
                  onClick={() => {
                    navigate(`/admin/financial-new?tab=invoices&dueMonth=${selectedMonth}`);
                    setSelectedPatient(null);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                  title="Ver faturas deste paciente"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ver Faturas do Paciente
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedPatient(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyClosingPage;
